import React, { useContext, useState } from 'react';
import { CellContext, ColumnDefTemplate } from '@tanstack/react-table';
import { Box, Chip } from '@mui/material';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';

import ScorecardButtonBase from 'Components/Scorecards/ScorecardTable/ScorecardOptions/ScorecardButtonBase';

import errorIcon from '@mui/icons-material/ErrorOutlineOutlined';
import {
    FirestoreUserRole,
    Invitation,
    InvitationStatus,
    useDeleteEmailInviteMutation,
} from 'generated/graphql';
import OperatorModal from '../../modal/OperatorModal';
import { useNotificationContext } from 'app/contexts/NotificationContext';
import UserContext from '../context/UsersContext';
import dayjs from 'dayjs';

export const InvitationStatusCell: ColumnDefTemplate<
    CellContext<Invitation, InvitationStatus>
> = ({ row, getValue }) => {
    const { expiresAt } = row.original;
    const status = getValue();

    if (dayjs().unix() > expiresAt) {
        return (
            <Chip
                label={'Expired'}
                variant="outlined"
                color={'info'}
                size="small"
            />
        );
    }

    switch (status) {
        case InvitationStatus.Accepted:
            return (
                <Chip
                    label="Accepted"
                    variant="outlined"
                    color="success"
                    size="small"
                />
            );

        case InvitationStatus.Pending:
            return (
                <Chip
                    label="Pending"
                    variant="outlined"
                    color="warning"
                    size="small"
                />
            );

        case InvitationStatus.Canceled:
            return <Chip label="Canceled" size="small" />;

        default:
            return <Chip label="-" />;
    }
};

export const InvitationsRoleCell: ColumnDefTemplate<
    CellContext<Invitation, FirestoreUserRole>
> = ({ getValue }) => {
    const role = getValue();

    switch (role) {
        case FirestoreUserRole.User:
            return 'User';

        case FirestoreUserRole.OrganizationAdmin:
            return 'Organization Admin';

        default:
            return 'Unknown role';
    }
};

export const ActionsCell: ColumnDefTemplate<
    CellContext<Invitation, string>
> = ({ row }) => {
    const { email: rowUserEmail, id: rowUserId } = row.original;

    const [showArchiveModal, setShowArchiveModal] = useState<boolean>(false);

    const { showNotification } = useNotificationContext();
    const { removeInvitation } = useContext(UserContext);

    const [deleteInvite, { loading }] = useDeleteEmailInviteMutation({
        onCompleted: () => {
            removeInvitation(rowUserId);
            showNotification({
                message: 'The invite has been successfully deleted!',
                severity: 'success',
            });
        },
        onError: () => {
            showNotification({
                message:
                    'There was an error while trying to delete this invite.',
                severity: 'error',
            });
        },
    });

    const handleOnClick = async () => {
        await deleteInvite({
            variables: {
                input: {
                    id: rowUserId,
                },
            },
        });

        setShowArchiveModal(false);
    };

    return (
        <Box display="flex" alignItems="center">
            <ScorecardButtonBase
                onClick={() => setShowArchiveModal(true)}
                startIcon={<ArchiveOutlinedIcon />}
            />

            <OperatorModal
                open={showArchiveModal}
                loading={loading}
                setOpen={setShowArchiveModal}
                Icon={errorIcon}
                headline="Are you sure?"
                description={`You are about to delete the invite for ${rowUserEmail}`}
                okayButtonLabel={`I understand, delete invite`}
                cancelButtonLabel="No. WAIT. CANCEL, CANCEL"
                customColor="rgb(182, 0, 0)"
                onClick={handleOnClick}
            />
        </Box>
    );
};
